import React, { useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, RiskFactorScores, FactorSeverity } from '../RiskAreasWidget';
import { ChevronRight, MapPin, AlertTriangle, Clock, CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantDSplit({
  areas,
  onSelect
}: Props) {
  const [previewArea, setPreviewArea] = useState<EnrichedRiskArea | null>(areas[0] || null);
  if (areas.length === 0) return <div className="flex flex-col items-center justify-center h-full text-center py-16" data-id="element-2911" data-mp="1aautvg7ruu89">
        <CheckCircle2 size={48} className="text-emerald-400 mb-4" data-id="element-2912" data-mp="15dl5m3w4com0" />
        <p className="text-lg font-medium text-gray-900" data-id="element-2913" data-mp="3frngpq6nqs4g">No activities found</p>
      </div>;
  return <div className="flex h-full overflow-hidden" data-id="element-2914" data-mp="3kqgxpsl5l19d">
      {/* Left: Narrow List */}
      <div className="w-1/3 min-w-[300px] border-r border-gray-200 overflow-y-auto bg-gray-50/50" data-id="element-2915" data-mp="3kodz4k5xpzt1">
        <div className="divide-y divide-gray-200" data-id="element-2916" data-mp="3pjtb16kdtlmt">
          {areas.map(area => {
          const config = RISK_LEVEL_CONFIG[area.riskLevel];
          const dataGap = hasDataGap(area);
          const isSelected = previewArea?.id === area.id;
          let rowClass = `w-full text-left p-4 transition-colors flex items-start gap-3 ${isSelected ? 'bg-white shadow-[inset_4px_0_0_0_#4f46e5]' : 'hover:bg-gray-100'}`;
          if (area.sifPotential) {
            rowClass = `w-full text-left p-4 transition-colors flex items-start gap-3 border-l-4 border-l-red-700 ${isSelected ? 'bg-red-50/80 shadow-[inset_4px_0_0_0_#b91c1c]' : 'bg-red-50/40 hover:bg-red-50/70'}`;
          } else if (dataGap) {
            rowClass = `w-full text-left p-4 transition-colors flex items-start gap-3 border-l-4 border-dashed border-l-amber-400 ${isSelected ? 'bg-amber-50/60 shadow-[inset_4px_0_0_0_#f59e0b]' : 'bg-amber-50/30 hover:bg-amber-50/50'}`;
          }
          return <button key={area.id} onClick={() => setPreviewArea(area)} className={rowClass} data-id="element-2917" data-mp="2w1jsci1sxldn">
                <div className={`flex-shrink-0 w-10 py-1 rounded ${config.bgColor} flex flex-col items-center`} data-id="element-2918" data-mp="23g0f5uxn2bw1">
                  <span className={`text-[10px] font-bold ${config.color}`} data-id="element-2919" data-mp="1zbkbm0bhzgjz">
                    {area.riskScore}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-2920" data-mp="28da3f79akhia">
                  <div className="flex items-center gap-1.5 mb-1" data-id="element-2921" data-mp="1kohazkrvorsf">
                    {area.sifPotential && <div className="mr-1" data-id="element-2922" data-mp="1fpxq7np0tm1n">
                        <SifBadge data-id="element-2923" data-mp="1fp4fycquseer" />
                      </div>}
                    <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-2924" data-mp="3rby87zucz00c">
                      {area.project}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2" data-id="element-2925" data-mp="3icl65s8kddpn">
                    <p className="text-xs text-gray-500 truncate" data-id="element-2926" data-mp="3nb5y74dnwr30">
                      {area.floor} · {area.location}
                    </p>
                    <ScheduledDateBadge date={area.scheduledDate} data-id="element-2927" data-mp="ewnvzjaek5rb" />
                  </div>
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Right: Preview Panel */}
      <div className="flex-1 bg-white flex flex-col h-full" data-id="element-2928" data-mp="4wnc9w8mfnml">
        {previewArea ? <>
            <div className="flex-1 overflow-y-auto p-8" data-id="element-2929" data-mp="9uf12hdyytmk">
              <div className="max-w-2xl mx-auto space-y-8" data-id="element-2930" data-mp="2pn4i16rz8iz8">
                {/* Header */}
                <div data-id="element-2931" data-mp="2un0duiqit43p">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2932" data-mp="2una3243qr78c">
                    <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${RISK_LEVEL_CONFIG[previewArea.riskLevel].bgColor}`} data-id="element-2933" data-mp="39vvq7rwbddf9">
                      <span className={`text-sm font-bold ${RISK_LEVEL_CONFIG[previewArea.riskLevel].color}`} data-id="element-2934" data-mp="313tpdsic1v4g">
                        Score: {previewArea.riskScore}
                      </span>
                      {/* Mini Sparkline */}
                      {previewArea.scoreHistory && previewArea.scoreHistory.length > 0 && <svg width="40" height="16" viewBox="0 0 40 16" className="opacity-60" data-id="element-2935" data-mp="3jubihy72usqc">
                            <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={previewArea.scoreHistory.slice(-7).map((h, i, arr) => {
                      const x = i / (arr.length - 1) * 40;
                      const min = Math.min(...arr.map(a => a.score));
                      const max = Math.max(...arr.map(a => a.score));
                      const range = max - min || 1;
                      const y = 16 - (h.score - min) / range * 16;
                      return `${x},${y}`;
                    }).join(' ')} data-id="element-2936" data-mp="28g6ckznb08sd" />
                          </svg>}
                    </div>
                    <span className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_CONFIG[previewArea.status].bgColor} ${STATUS_CONFIG[previewArea.status].color}`} data-id="element-2937" data-mp="wmnhyo1yjgwf">
                      {createElement(STATUS_CONFIG[previewArea.status].icon, {
                    size: 12
                  })}
                      {STATUS_CONFIG[previewArea.status].label}
                    </span>
                    <DataConfidenceBadge confidence={previewArea.dataConfidence} data-id="element-2938" data-mp="17jiqxxrfs9qy" />
                    {previewArea.sifPotential && <SifBadge data-id="element-2939" data-mp="12iuxl5fqjgtk" />}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2940" data-mp="1h8sfx4qzhcau">
                    {previewArea.project}
                  </h2>
                  <p className="text-gray-500 flex items-center gap-2" data-id="element-2941" data-mp="1ccty6005usje">
                    <MapPin size={16} data-id="element-2942" data-mp="1qbbj5pyolqxm" /> {previewArea.floor} —{' '}
                    {previewArea.location}
                  </p>
                </div>

                {/* Contacts */}
                {previewArea.contacts && previewArea.contacts.length > 0 && <div className="flex flex-wrap gap-4 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-2943" data-mp="29wtttdkqzj10">
                    {previewArea.contacts.slice(0, 2).map((contact, i) => <div key={i} className="flex items-center gap-2 text-sm" data-id="element-2944" data-mp="20vlv4uebkg6e" data-mpi={i}>
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs" data-id="element-2945" data-mp="20xq6tyigmjh8">
                          {contact.name.charAt(0)}
                        </div>
                        <div data-id="element-2946" data-mp="1d6b4w6uli46i">
                          <p className="font-medium text-gray-900" data-id="element-2947" data-mp="1d72nmh33g7fd">
                            {contact.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-2948" data-mp="1h5jt6w6v99uo">
                            {contact.role}
                          </p>
                        </div>
                        <a href={`tel:${contact.phone}`} className="ml-2 p-1.5 bg-white rounded-full shadow-sm text-gray-400 hover:text-indigo-600 transition-colors" data-id="element-2949" data-mp="185n4iukh8azm">
                          <Phone size={14} data-id="element-2950" data-mp="1eo9ptsi782cl" />
                        </a>
                      </div>)}
                  </div>}

                {/* Top Factors */}
                <div data-id="element-2951" data-mp="14q3nxawe6w7q">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-2952" data-mp="14qn8kzdffypg">
                    Risk Drivers
                  </h3>
                  <div className="space-y-2" data-id="element-2953" data-mp="zy2ctsaynbja">
                    {previewArea.riskFactors.slice(0, 3).map((factor, i) => <div key={i} className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg" data-id="element-2954" data-mp="qyp7nbtvd94d" data-mpi={i}>
                        <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" data-id="element-2955" data-mp="lyuc2uz5fvcz" />
                        <span className="text-sm text-gray-700" data-id="element-2956" data-mp="2sn32ke7r7p80">{factor}</span>
                      </div>)}
                  </div>
                </div>

                {/* Recent Events */}
                {previewArea.events && previewArea.events.length > 0 && <div data-id="element-2957" data-mp="36mmb384h92ku">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-2958" data-mp="36n4661felar6">
                      Recent Events
                    </h3>
                    <div className="space-y-3" data-id="element-2959" data-mp="2evy8rsr2hr5l">
                      {previewArea.events.slice(0, 2).map(event => <div key={event.id} className="flex items-start gap-3" data-id="element-2960" data-mp="2ix9s5gjclg5c">
                          <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" data-id="element-2961" data-mp="2np1l1xpz7xyn" />
                          <div data-id="element-2962" data-mp="1r7orn44strks">
                            <p className="text-sm text-gray-800" data-id="element-2963" data-mp="1r6n7891w7sqe">
                              {event.description}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5" data-id="element-2964" data-mp="3w2fe7ah98gv4">
                              {event.date.toLocaleDateString()}{' '}
                              {event.date.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>}

                {/* Primary Action */}
                {previewArea.correctiveActions.length > 0 && <div data-id="element-2965" data-mp="3mbb16gb5362x">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-2966" data-mp="3marqnkntjjyx">
                      Recommended Action
                    </h3>
                    <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl" data-id="element-2967" data-mp="2tmdw5evxv5s4">
                      <p className="font-medium text-indigo-900 mb-2" data-id="element-2968" data-mp="2ylqfvqrijwqh">
                        {previewArea.correctiveActions[0].action}
                      </p>
                      {previewArea.correctiveActions[0].rationale && <p className="text-sm text-indigo-700 mb-3" data-id="element-2969" data-mp="2m4pe72ega9wp">
                          {previewArea.correctiveActions[0].rationale}
                        </p>}
                      {previewArea.correctiveActions[0].expectedImpactPercent && <div className="inline-flex items-center gap-1 bg-white text-indigo-600 text-xs font-bold px-2 py-1 rounded shadow-sm" data-id="element-2970" data-mp="285g0c9dpvrvw">
                          Est. Impact: -
                          {previewArea.correctiveActions[0].expectedImpactPercent}
                          % Risk
                        </div>}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Full Details Button (Sticky Footer) */}
            <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50" data-id="element-2971" data-mp="1apn60iuigvtb">
              <div className="max-w-2xl mx-auto" data-id="element-2972" data-mp="1eu3b4hwavz7h">
                <button onClick={() => onSelect(previewArea)} className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-3.5 rounded-xl font-medium hover:bg-gray-800 transition-colors shadow-sm" data-id="element-2973" data-mp="10t0n3zlqwetx">
                  View Full Details & Take Action <ArrowRight size={18} data-id="element-2974" data-mp="1yh0x6lgxelcp" />
                </button>
              </div>
            </div>
          </> : <div className="flex items-center justify-center h-full text-gray-400" data-id="element-2975" data-mp="1jqtqf30a6qru">
            Select a risk area to preview
          </div>}
      </div>
    </div>;
}